import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronRight, BellRing, MapPin } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useI18n } from "@/lib/i18n";
import { useAuth, Spinner } from "@/lib/auth";
import { BuildLeadCard } from "@/pages/owner/OwnerBuilds";
import { api, fmtDate } from "@/lib/api";
import { StatusBadge, ContactButtons } from "@/components/common";

function JobRow({ r, to, lead }) {
  const { t } = useI18n();
  const inner = (
    <>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-sm font-semibold">{r.number}</span>
          {!lead && <StatusBadge status={r.status} />}
          {r.urgent && <span className="inline-flex items-center gap-1 text-xs text-red-700 font-semibold"><AlertTriangle className="h-3.5 w-3.5" />{t("d.urgent")}</span>}
          {!lead && r.assignment?.status === "pending" && <span className="text-xs font-semibold text-amber-700">{t("d.respond")}</span>}
        </div>
        <p className="text-sm text-slate-700 mt-1">{t(r.kind === "cleaning" ? "cl.label" : `svc.${r.service}`)} · <MapPin className="inline h-3 w-3" /> {[r.city, r.neighborhood].filter(Boolean).join(", ")}</p>
        <p className="text-sm text-slate-500 line-clamp-2 whitespace-pre-line">{r.description}</p>
        <p className="text-[10px] font-mono text-slate-400">{fmtDate(r.created_at)}</p>
      </div>
      {to && <ChevronRight className="h-5 w-5 text-slate-400 rtl:rotate-180" />}
    </>
  );
  const cls = "lift flex items-center gap-4 rounded-2xl bg-white border border-slate-200 p-5";
  if (!to) return <div className={cls} data-testid={`lead-row-${r.number}`}>{inner}</div>;
  return (
    <div className="relative">
      <Link to={to} className={cls} data-testid={`job-row-${r.number}`}>{inner}</Link>
      {r.assignment?.status === "accepted" && r.contact_phone && (
        <div className="absolute bottom-4 end-12" data-testid={`job-contact-${r.number}`}><ContactButtons phone={r.contact_phone} compact text={`${r.contact_name}, NicaFix ${r.number}`} /></div>
      )}
    </div>
  );
}

export default function ContractorDashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [jobs, setJobs] = useState(null);
  const [leads, setLeads] = useState([]);
  const [profile, setProfile] = useState(null);
  const [builds, setBuilds] = useState([]);
  const [builders, setBuilders] = useState([]);
  const loadBuilds = () => api.get("/contractor/build-leads").then((r) => setBuilds(r.data));
  useEffect(() => {
    loadBuilds();
    api.get("/builders").then((r) => setBuilders(r.data));
    api.get("/contractor/jobs").then((r) => setJobs(r.data));
    api.get("/contractor/leads").then((r) => setLeads(r.data));
    api.get("/contractor/profile").then((r) => setProfile(r.data));
  }, []);
  if (!jobs) return <Spinner />;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="contractor-dashboard">
      <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-teal-700">{t("d.hello", { name: user.name })}</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">{t("d.jobs")}</h1>
          <Tabs defaultValue="jobs" className="mt-6">
            <TabsList>
              <TabsTrigger value="jobs" data-testid="contractor-tab-jobs">{t("d.jobs")} ({jobs.length})</TabsTrigger>
              <TabsTrigger value="leads" data-testid="contractor-tab-leads">{t("d.leads")} ({leads.length})</TabsTrigger>
              {builds.length > 0 && <TabsTrigger value="builds" data-testid="contractor-tab-builds">{t("bl.mine")} ({builds.length})</TabsTrigger>}
            </TabsList>
            <TabsContent value="builds" className="space-y-3 mt-4">
              {builds.map((l) => <BuildLeadCard key={l.id} l={l} builders={builders} reload={loadBuilds} />)}
            </TabsContent>
            <TabsContent value="jobs" className="space-y-3 mt-4">
              {jobs.length === 0 && <p className="text-slate-500 py-6">{t("d.empty")}</p>}
              {jobs.map((r) => <JobRow key={r.id} r={r} to={`/contractor/jobs/${r.id}`} />)}
            </TabsContent>
            <TabsContent value="leads" className="space-y-3 mt-4">
              <p className="text-sm text-slate-500 flex items-center gap-2"><BellRing className="h-4 w-4 text-amber-500" />{t("d.leads_d")}</p>
              {leads.length === 0 && <p className="text-slate-500 py-6">{t("d.empty")}</p>}
              {leads.map((r) => <JobRow key={r.id} r={r} lead />)}
            </TabsContent>
          </Tabs>
        </div>
        {profile && (
          <aside className="rounded-3xl bg-slate-900 text-white p-6" data-testid="contractor-profile">
            <p className="text-lg font-bold">{profile.name}</p>
            <p className="text-xs uppercase tracking-wider text-teal-300 mt-1">{t(`o.s_${profile.status}`)}</p>
            <p className="mt-4 text-xs text-slate-400">{t("f.services")}</p>
            <div className="flex flex-wrap gap-1.5 mt-1">{profile.services.map((s) => <span key={s} className="text-xs bg-white/10 rounded-full px-2 py-0.5">{t(`svc.${s}`)}</span>)}</div>
            <p className="mt-4 text-xs text-slate-400">{t("f.area")}</p>
            <p className="text-sm">{profile.service_areas.join(", ")}</p>
            <p className="mt-4 text-sm text-amber-300">{t("d.jobs_done", { n: profile.jobs_completed })}</p>
          </aside>
        )}
      </div>
    </div>
  );
}
